import type { Domain } from '@/types/content'

/**
 * The three domains of practice, each with the product types that live under
 * it. Every piece of work is classified into one or more of these, so the
 * portfolio can be read either as "what he does" or "what he has built".
 *
 * Adding a category here is enough for it to appear as a filter — the work
 * pages derive their grouping from this list rather than hardcoding it.
 */
export const domains: readonly Domain[] = [
  {
    id: 'software',
    name: 'Software Engineering',
    // Code, not a browser window: two of the three categories under this
    // domain are not web apps at all.
    icon: 'codeBlock',
    description:
      'Applications built end to end — the interface, the API behind it, and the database under that.',
    categories: [
      { id: 'web-app', name: 'Web Apps' },
      { id: 'mobile-app', name: 'Mobile Apps' },
      { id: 'desktop-app', name: 'Desktop Apps' },
    ],
  },
  {
    id: 'design',
    name: 'Design Works',
    // A palette. The atom that was here said "science", which is the one
    // thing this domain is not.
    icon: 'palette',
    description:
      'Visual identity and interface design: the mark, the system it belongs to, and the screens it ends up on.',
    categories: [
      { id: 'logo', name: 'Logos' },
      { id: 'flyer', name: 'Flyers' },
      { id: 'design-system', name: 'Design Systems' },
      { id: 'web-design', name: 'Web Design' },
    ],
  },
  {
    id: 'infrastructure',
    name: 'Networking & Cloud',
    // A cloud with a link through it. The archive box that was here read as
    // storage, which is a small corner of what this domain covers.
    icon: 'cloudNetwork',
    description:
      'The systems underneath: how a platform is deployed, how it scales, and how the network carrying it is laid out.',
    categories: [
      { id: 'deployment-architecture', name: 'Deployment Architectures' },
      { id: 'network-architecture', name: 'Network Architectures' },
    ],
  },
]

/** Lookup for a domain by id. */
export const findDomain = (id: string): Domain | undefined =>
  domains.find((domain) => domain.id === id)

/** Human-readable name for a category within a domain. */
export const categoryName = (domainId: string, categoryId: string): string =>
  findDomain(domainId)?.categories.find((c) => c.id === categoryId)?.name ??
  categoryId
